import { useCallback, useContext, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from "react-router-dom";
import { FaQrcode, FaList, FaArrowLeft } from 'react-icons/fa';

import useReader from '../../hooks/use-reader';
import useLogisticsApi from '../../hooks/use-logistics-api';
import { useCourierCacheInvalidation, useWarehouseOutgoingInvalidation } from '../../hooks/use-queries';
import { useProcessedRoutes, setOptimisticStop } from '../../hooks/use-processed-routes';
import { useQueryBridge } from '../../hooks/use-query-bridge';

import AppContext from '../../store/app-context';

import { ActionBar, ActionButton } from '../../components/Shared/ActionBar/ActionBar';
import BoxList from '../../components/Shared/BoxList/BoxList';

import { EnhancedOrderBox } from '../../types/logistics';

import './CourierWarehouse.scss';

interface BoxGroup {
    routeIndex: number | undefined;
    label: string;
    boxes: EnhancedOrderBox[];
}

function groupBoxesByRoute(boxes: EnhancedOrderBox[]): BoxGroup[] {
    const groups = new Map<number | undefined, BoxGroup>();

    for (const box of boxes) {
        const key = box.routeIndex;
        if (!groups.has(key)) {
            groups.set(key, {
                routeIndex: key,
                label: box.orderCustomerName || (key != null ? `Stop ${key + 1}` : 'Other'),
                boxes: [],
            });
        }
        groups.get(key)!.boxes.push(box);
    }

    // Return groups in array order (boxes are already sorted descending by routeIndex)
    const result: BoxGroup[] = [];
    const seen = new Set<number | undefined>();
    for (const box of boxes) {
        const key = box.routeIndex;
        if (!seen.has(key)) {
            seen.add(key);
            result.push(groups.get(key)!);
        }
    }
    return result;
}

const CourierWarehouse = () => {
    const appCtx = useContext(AppContext);

    const navigate = useNavigate();
    const params = useParams<{ warehouse: string }>();
    const warehouseKey = params.warehouse as 'load' | 'unload';

    const [manualMode, setManualMode] = useState(false);

    const { startQuagga } = useReader(null);
    const { updateBoxStatus } = useLogisticsApi();
    const { invalidateCourierCache } = useCourierCacheInvalidation();
    const { invalidateWarehouseOutgoing } = useWarehouseOutgoingInvalidation();

    // Derive load/unload data from shared hook (React Query cache)
    const { load: serverLoad, stops, isLoading, error, hasData } = useProcessedRoutes();

    // Bridge loading/error to global overlay (only for initial load)
    useQueryBridge(isLoading, error, 'Loading warehouse...', 'Failed to load warehouse', hasData);

    // Local state for optimistic scan updates (scanned flag + progress)
    const [localBoxOverrides, setLocalBoxOverrides] = useState<Map<number, Partial<EnhancedOrderBox>>>(new Map());

    const warehouseData = useMemo(() => {
        if (warehouseKey !== 'load') return null;
        if (!serverLoad || serverLoad.boxes.length === 0) return null;

        const boxes = serverLoad.boxes.map(b =>
            localBoxOverrides.has(b.id) ? { ...b, ...localBoxOverrides.get(b.id) } : b
        );
        const progress = boxes.filter(b => b.scanned).length;
        return { boxes, progress };
    }, [warehouseKey, serverLoad, localBoxOverrides]);

    const geolocation = appCtx.geolocation;

    // Use refs to access latest values in callbacks without triggering re-renders
    const warehouseDataRef = useRef(warehouseData);
    warehouseDataRef.current = warehouseData;

    // Per-box duplicate guard — prevent same box being processed concurrently
    const processingBoxIds = useRef(new Set<number>());

    // Ref to latest stops for use in async callbacks
    const stopsRef = useRef(stops);
    stopsRef.current = stops;

    const updateBox = useCallback(async (box: any) => {
        // Prevent duplicate scan of same box
        if (box.id && processingBoxIds.current.has(box.id)) return;

        // Update local state optimistically
        if (box.id) {
            setLocalBoxOverrides(prev => {
                const next = new Map(prev);
                next.set(box.id, { scanned: box.scanned });
                return next;
            });
        }

        // Update status on backend ONLY for 'load' action
        if (box.scanned && warehouseKey === 'load') {
            if (box.id) processingBoxIds.current.add(box.id);
            try {
                // load = ITTC (In Transit To Customer - courier loading for delivery)
                const statusCode = 'ITTC';

                await updateBoxStatus(box.id, statusCode, {
                    latitude: geolocation.latitude ? parseFloat(geolocation.latitude) : undefined,
                    longitude: geolocation.longitude ? parseFloat(geolocation.longitude) : undefined
                });

                // Write optimistic overlay for the delivery stop that contains this box.
                // This ensures Courier's load button count updates instantly when navigating back.
                const deliveryStop = stopsRef.current.find(
                    s => s.type === 'delivery' && s.boxes.some(b => b.id === box.id)
                );
                if (deliveryStop) {
                    const updatedStop = {
                        ...deliveryStop,
                        boxes: deliveryStop.boxes.map(b =>
                            b.id === box.id ? { ...b, statusCode: 'ITTC' as any, scanned: true, isCompleted: true } : b
                        ),
                    };
                    updatedStop.progress = updatedStop.boxes.filter(b => b.isCompleted).length;
                    setOptimisticStop(updatedStop);
                }

                invalidateCourierCache();
                invalidateWarehouseOutgoing(); // WKKHO→ITTC affects outgoing inventory
            } catch (error) {
                console.warn('Failed to update box status (queued for sync):', error);
            } finally {
                if (box.id) processingBoxIds.current.delete(box.id);
            }
        }
    }, [warehouseKey, updateBoxStatus, geolocation, invalidateCourierCache, invalidateWarehouseOutgoing]);

    // Use a ref to hold updateBox so startQuagga doesn't depend on it
    const updateBoxRef = useRef(updateBox);
    updateBoxRef.current = updateBox;

    // Redirect if trying to access unload page
    if (warehouseKey === 'unload') {
        navigate('/courier');
        return null;
    }

    const handleStartScan = () => {
        setManualMode(false);
        const currentData = warehouseDataRef.current;
        startQuagga({
            boxes: currentData?.boxes || [],
            update: updateBoxRef.current
        });
    };

    // Display friendly name for action
    const actionDisplayName = warehouseKey === 'load' ? 'load' : 'arrived';

    // Group load boxes by route stop for loading order guidance
    const boxGroups = warehouseKey === 'load' && warehouseData?.boxes
        ? groupBoxesByRoute(warehouseData.boxes)
        : [];
    const hasGroups = boxGroups.length > 1 || (boxGroups.length === 1 && boxGroups[0].routeIndex != null);

    if (!warehouseData && hasData && !isLoading) {
        return (
            <section>
                <button className="btn-back-to-route" onClick={() => navigate('/courier')}>
                    <FaArrowLeft /> Back to Route
                </button>
                <div className="empty-state">
                    <p>No boxes to load. All boxes may already be loaded or there are no deliveries today.</p>
                </div>
            </section>
        );
    }

    return (
        <>
            {warehouseData?.boxes &&
                <>
                    <h1>
                        Warehouse {actionDisplayName} ({warehouseData.progress || 0} of {warehouseData.boxes.length})
                    </h1>
                    {warehouseKey === 'load' && (
                        <p className="help-text">
                            {hasGroups
                                ? 'Load boxes in order below — last delivery goes in first (bottom of truck)'
                                : 'Scan WKKHO boxes to load into your vehicle for delivery'}
                        </p>
                    )}
                    {hasGroups ? (
                        boxGroups.map((group, groupIdx) => (
                            <div key={group.routeIndex ?? 'other'} className="load-group">
                                <div className="load-group-header">
                                    <span className="load-group-order">
                                        {group.routeIndex != null
                                            ? (groupIdx === 0
                                                ? `Stop ${group.routeIndex + 1} (last delivery)`
                                                : groupIdx === boxGroups.length - 1
                                                    ? `Stop ${group.routeIndex + 1} (first delivery)`
                                                    : `Stop ${group.routeIndex + 1}`)
                                            : 'Other'}
                                    </span>
                                    <span className="load-group-customer">{group.label}</span>
                                </div>
                                <BoxList
                                    boxes={group.boxes}
                                    progress={group.boxes.filter((b: any) => b.scanned).length}
                                    update={updateBox}
                                    manualMode={manualMode}
                                />
                            </div>
                        ))
                    ) : (
                        <BoxList
                            boxes={warehouseData.boxes}
                            progress={warehouseData.progress || 0}
                            update={updateBox}
                            manualMode={manualMode}
                        />
                    )}
                </>
            }

            {/* Action Bar */}
            {warehouseData?.boxes && (
                <ActionBar>
                    <ActionButton variant="primary" icon={<FaQrcode />} onClick={handleStartScan}>
                        Scan
                    </ActionButton>
                    <ActionButton variant={manualMode ? 'active' : 'default'} icon={<FaList />} onClick={() => setManualMode(prev => !prev)}>
                        {manualMode ? 'Exit Manual' : 'Manual'}
                    </ActionButton>
                </ActionBar>
            )}
        </>
    );
};

export default CourierWarehouse;
